import { useQuery } from '@apollo/client';
import { GET_SUPPORT_SESSION } from '@employ-engage/common-components';
import { useEffect, useState } from 'react';

/**
 * Polling interval that drops to 0 (Apollo stops polling) while the tab is in
 * the background. Every signed-in tab polls, so idle tabs are most of it.
 */
export const useVisiblePollInterval = (intervalMs: number) => {
  const [visible, setVisible] = useState(
    () => typeof document === 'undefined' || !document.hidden
  );
  useEffect(() => {
    const onChange = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return visible ? intervalMs : 0;
};

export type SupportSessionInfo = {
  requestId: string;
  adminEmail: string;
  targetEmail: string;
  accessExpiresAt: string | null;
  isActive: boolean;
};

/**
 * The support session this browser is in, when a super admin is acting as
 * the signed-in user. Reads the shared cache; SupportSessionBanner keeps it
 * fresh.
 */
export const useSupportSession = (options: { pollInterval?: number } = {}) => {
  const { data, loading } = useQuery(GET_SUPPORT_SESSION, {
    fetchPolicy: 'cache-and-network',
    ...options,
  });
  return {
    supportSession: (data?.supportSession ?? null) as SupportSessionInfo | null,
    loading,
  };
};

/** True while a super admin is acting as this user. */
export const useIsSupportSession = () => !!useSupportSession().supportSession;
